import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Body1Strong,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
} from '@fluentui/react-components';
import { ErrorCircleFilled, DismissRegular } from '@fluentui/react-icons';
import {
  notificationService,
  Notification,
} from '../services/notificationService';
import { useMSALAuth } from '../contexts/MSALAuthContext';

const POLL_INTERVAL_MS = 30_000;

const TEAMS_ADMIN_CENTER_URL = 'https://admin.teams.microsoft.com';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    marginBottom: '16px',
  },
  messageBar: {
    width: '100%',
  },
});

export const NotificationBanner: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isAuthReady, user } = useMSALAuth();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !isAuthReady || !user) return;
    try {
      const data = await notificationService.getNotifications();
      setNotifications(data);
    } catch {
      // Silently fail; banner simply stays hidden
    }
  }, [isAuthenticated, isAuthReady, user]);

  useEffect(() => {
    if (!isAuthenticated || !isAuthReady || !user) return;

    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAuthReady, user, fetchNotifications]);

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
    notificationService.markAsRead(id);
  };

  const visible = notifications
    .filter(
      (n) =>
        !dismissedIds.has(n.id) &&
        n.entity === 'policy' &&
        n.entity_action === 'delete' &&
        n.entity_action_type === 'failure'
    )

  if (visible.length === 0) return null;

  return (
    <div className={styles.container} data-testid="notification-banner-container">
      {visible.map((notification) => (
          <MessageBar
            key={notification.id}
            className={styles.messageBar}
            intent="error"
            icon={<ErrorCircleFilled />}
            data-testid={`notification-banner-${notification.id}`}
          >
            <MessageBarBody data-testid="notification-banner-body">
              <span style={{ fontSize: '14px', color: '#8A001E' }}>
                <Body1Strong>We were unable to delete the policy '{notification.entity_id}' as it was assigned outside of the ADM.</Body1Strong> Go to Teams Admin Center to delete manually.
              </span>
            </MessageBarBody>
            <MessageBarActions
              data-testid="notification-banner-actions"
              containerAction={
                <Button
                  aria-label="dismiss"
                  appearance="transparent"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={() => handleDismiss(notification.id)}
                  style={{ color: '#8A001E' }}
                  data-testid={`notification-banner-dismiss-${notification.id}`}
                />
              }
            >
              <Button
                as="a"
                href={TEAMS_ADMIN_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                appearance="secondary"
                size="small"
                data-testid="notification-banner-teams-link"
              >
                Open Teams Admin Center
              </Button>
            </MessageBarActions>
          </MessageBar>
        ))}

    </div>
  );
};

export default NotificationBanner;
